import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Bell, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

import { BACKEND_URL } from '../../config/api';
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('lms_token')}`
});

export default function CreateAnnouncement({ user, onLogout }) {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    priority: 'normal'
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  const fetchAnnouncements = async () => {
    try {
      const res = await axios.get(`${API}/announcements`, { headers: getAuthHeaders() });
      setAnnouncements(res.data);
    } catch (error) {
      console.error('Failed to fetch announcements:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim() || !formData.content.trim()) {
      toast.error('Please fill in all fields');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/announcements`, formData, { headers: getAuthHeaders() });
      toast.success('Announcement created successfully!');
      setFormData({ title: '', content: '', priority: 'normal' });
      setShowForm(false);
      fetchAnnouncements();
    } catch (error) {
      console.error('Failed to create announcement:', error);
      toast.error('Failed to create announcement');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this announcement?')) return;

    try {
      await axios.delete(`${API}/announcements/${id}`, { headers: getAuthHeaders() });
      toast.success('Announcement deleted');
      fetchAnnouncements();
    } catch (error) {
      console.error('Failed to delete announcement:', error);
      toast.error('Failed to delete announcement');
    }
  };

  if (loading) {
    return <Layout user={user} onLogout={onLogout} currentPage="create-announcement"><div className="p-8">Loading...</div></Layout>;
  }

  return (
    <Layout user={user} onLogout={onLogout} currentPage="create-announcement">
      <div className="p-6 lg:p-8 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl lg:text-4xl font-bold text-navy">📣 Announcements</h1>
            <p className="text-[#4D4D4D] mt-2 font-light">View, create, and manage announcements</p>
          </div>
          <Button
            onClick={() => setShowForm(!showForm)}
            className="bg-primary-blue hover:bg-[#1874CD] text-white font-semibold rounded-xl transition-smooth btn-press"
          >
            {showForm ? 'Cancel' : '+ New Announcement'}
          </Button>
        </div>

        {showForm && (
          <Card className="border-0 card-shadow bg-white rounded-2xl">
            <CardContent className="p-6">
              <h3 className="text-xl font-bold text-navy mb-4">Create Announcement</h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-navy mb-2">Title</label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Announcement title"
                    className="h-12 border-2 border-[#E7E7E7] rounded-xl focus:border-primary-blue transition-smooth"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-navy mb-2">Content</label>
                  <Textarea
                    value={formData.content}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                    placeholder="Announcement details..."
                    rows={5}
                    className="border-2 border-[#E7E7E7] rounded-xl focus:border-primary-blue transition-smooth"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-primary-blue hover:bg-[#1874CD] text-white font-semibold rounded-xl transition-smooth btn-press"
                >
                  {submitting ? 'Creating...' : 'Create Announcement'}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        <div className="space-y-3">
          {announcements.length === 0 ? (
            <Card className="border-0 card-shadow bg-white rounded-2xl">
              <CardContent className="p-12 text-center">
                <Bell className="w-20 h-20 mx-auto mb-4 text-[#E7E7E7]" strokeWidth={1.5} />
                <p className="text-xl font-bold text-navy">No announcements yet</p>
              </CardContent>
            </Card>
          ) : (
            announcements.map((announcement) => (
              <Card key={announcement.id} className="border-0 card-shadow bg-white rounded-2xl card-hover transition-smooth">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-navy mb-2">{announcement.title}</h3>
                      <p className="text-[#4D4D4D] mb-2">{announcement.content}</p>
                      <p className="text-sm text-[#A5A5A5]">
                        {new Date(announcement.created_at).toLocaleString()}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(announcement.id)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 rounded-xl transition-smooth"
                    >
                      <Trash2 className="w-4 h-4" strokeWidth={2} />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    </Layout>
  );
}